/**
 * smsAyarService.js — Sistem Ayarları › SMS sayfalarının uçları (backlog #637; backend #636 yönetim API'si, #634 şablonlar, #635 rapor / bakiye / gönderen adı).
 *
 * ⚠ SÖZLEŞME VARSAYIMDIR: #636 / #634 / #635 henüz yazılmadı. Aşağıdaki yollar ve alan adları bu görevlerin metninden ve `sms_account` / `sms_log`
 * kolonlarından (V123 / V124) çıkarıldı, e-posta yönetim API'sinin (`/email/*`) biçimiyle hizalandı; backend yazılırken ya bunlara uyulur ya da bu dosya
 * ve testi birlikte güncellenir. Hesabın sırları (kno, kulad, şifre) YALNIZ yazılır: liste `has_kno / has_kulad / has_password` döner, boş bir sır alanı
 * güncellemede "değişmez" demektir. Her hata `{ message, messageKey }` (isteğin dilinde); sayfalar `message`'ı gösterir.
 *
 *   GET    /sms/accounts                       → [{ sms_account_id, sms_account_title, sms_account_vendor, sms_account_sender, is_default, is_active,
 *                                                  has_kno, has_kulad, has_password, sms_account_blocked_at, sms_account_blocked_reason, updated_at }]
 *   POST   /sms/accounts            · PUT /sms/accounts/{id}   gövde { sms_account_title, sms_account_sender, sms_account_kno, sms_account_kulad,
 *                                                  sms_account_sifre, is_default, is_active }  (SYSTEM_SETTINGS_MANAGE)
 *   DELETE /sms/accounts/{id}
 *   POST   /sms/accounts/{id}/test  { to? }    → { status: SENT|FAILED|BLOCKED, to, message, error, logId } — `to` yoksa isteği yapanın doğrulanmış telefonu;
 *                                                güvenli mod geçerli, saatte 5 (429), kredi harcar. Engelli (devre kesici) bir hesapta da denenebilir; BAŞARILI
 *                                                test engeli kaldırır (backend SmsAccountBreaker.reset), sayfa listeyi yeniden okur.
 *   GET    /sms/senders · POST /sms/senders/refresh · POST /sms/senders { name }  → gönderen adları ve onay durumu (APPROVED | PENDING | REJECTED)
 *   GET    /sms/balance                        → { balance, unit_price, currency, checked_at, low_threshold, low, no_credit }
 *   POST   /sms/balance/refresh                → aynı şekil (satıcıdan şimdi oku)
 *   GET    /sms/logs?page=&size=&status=&category=&template_key=&from=&to=&q=  → { content:[...], totalElements, totalPages, number, size } (ya da düz dizi)
 *   GET    /sms/logs/{id}                      → tek satır (kod / geçici şifre ASLA yok; metin maskeli)
 *   GET    /sms/templates/system               → [{ key, description, category, text_tr, text_en, active, placeholders:[..], required:[..], otp }]
 *   PUT    /sms/templates/{key}   { text_tr, text_en, active }   (SYSTEM_SETTINGS_MANAGE; 400: bilinmeyen yer tutucu, eksik KOD, uzun, kara listedeki karakter)
 *   GET    /sms/templates/{key}/preview?language=tr|en  → { text, length, weighted, parts }
 *   GET    /sms/report-secret · POST /sms/report-secret/rotate → { url, rotated_at } (push rapor adresi; adres YALNIZ döndürüldüğü anda tam görünür)
 * Anahtarlar (sms.etkin, sms.mfa.etkin, sms.duyuru.etkin, sms.gecici_panel.etkin) Sistem Ayarları'nın `sms` grubundadır (systemSettingsService).
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/sms`;

export function smsHatasi(error, yedek) {
  const govde = error?.response?.data;
  const mesaj = govde && typeof govde === 'object' ? (govde.message || govde.error) : null;
  const e = new Error(mesaj || error?.message || yedek || 'Unknown error');
  e.status = error?.response?.status ?? null;
  e.messageKey = govde && typeof govde === 'object' ? (govde.messageKey || null) : null;
  e.data = govde && typeof govde === 'object' ? (govde.data ?? null) : null;
  return e;
}

async function al(istek) {
  try {
    return (await istek()).data;
  } catch (error) {
    throw smsHatasi(error);
  }
}

const dizi = (veri) => (Array.isArray(veri) ? veri : Array.isArray(veri?.content) ? veri.content : Array.isArray(veri?.data) ? veri.data : []);

/** Sayfalı cevabı { satirlar, toplam, sayfaSayisi } biçimine indirger (düz dizi de olur). */
export function sayfala(veri, sayfaBoyutu = 20) {
  const satirlar = dizi(veri);
  const toplam = Number.isFinite(veri?.totalElements) ? veri.totalElements : satirlar.length;
  const sayfaSayisi = Number.isFinite(veri?.totalPages) ? veri.totalPages : Math.max(1, Math.ceil(toplam / sayfaBoyutu));
  return { satirlar, toplam, sayfaSayisi };
}

const headers = () => ({ headers: getAuthHeader() });

// ── Hesaplar ──
export async function listAccounts() { return dizi(await al(() => axios.get(`${BASE}/accounts`, headers()))); }
export const createAccount = (form) => al(() => axios.post(`${BASE}/accounts`, form, headers()));
export const updateAccount = (id, form) => al(() => axios.put(`${BASE}/accounts/${id}`, form, headers()));
export const deleteAccount = (id) => al(() => axios.delete(`${BASE}/accounts/${id}`, headers()));
/** Test SMS; `to` boşsa isteği yapanın doğrulanmış telefonu. */
export const testAccount = (id, to) => al(() => axios.post(`${BASE}/accounts/${id}/test`, to && to.trim() ? { to: to.trim() } : {}, headers()));

// ── Gönderen adları ──
export async function listSenders() { return dizi(await al(() => axios.get(`${BASE}/senders`, headers()))); }
export async function refreshSenders() { return dizi(await al(() => axios.post(`${BASE}/senders/refresh`, {}, headers()))); }
export const applySender = (name) => al(() => axios.post(`${BASE}/senders`, { name: name.trim() }, headers()));

// ── Bakiye ve rapor adresi ──
export const getBalance = () => al(() => axios.get(`${BASE}/balance`, headers()));
export const refreshBalance = () => al(() => axios.post(`${BASE}/balance/refresh`, {}, headers()));
export const getReportSecret = () => al(() => axios.get(`${BASE}/report-secret`, headers()));
export const rotateReportSecret = () => al(() => axios.post(`${BASE}/report-secret/rotate`, {}, headers()));

// ── Kayıtlar ──
export function listLogs({ page = 0, size = 20, status, category, templateKey, from, to, q } = {}) {
  const params = { page, size };
  if (status) params.status = status;
  if (category) params.category = category;
  if (templateKey) params.template_key = templateKey;
  if (from) params.from = from;
  if (to) params.to = to;
  if (q && q.trim()) params.q = q.trim();
  return al(() => axios.get(`${BASE}/logs`, { ...headers(), params }));
}
export const getLog = (id) => al(() => axios.get(`${BASE}/logs/${id}`, headers()));

// ── Şablonlar ──
export async function listSystemSmsTemplates() { return dizi(await al(() => axios.get(`${BASE}/templates/system`, headers()))); }
export const updateSmsTemplate = (key, { textTr, textEn, active }) =>
  al(() => axios.put(`${BASE}/templates/${encodeURIComponent(key)}`, { text_tr: textTr, text_en: textEn || '', active }, headers()));
export const previewSmsTemplate = (key, language) =>
  al(() => axios.get(`${BASE}/templates/${encodeURIComponent(key)}/preview`, { ...headers(), params: { language } }));
